import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import TitleSubtitle from "../../components/common/TitleSubtitle";
import Dropdown from "../../components/common/Dropdown";
import TextInput from "../../components/ui/TextInput";
import Icon from "../../components/common/Icon";
import { getAppUsers, updateAppUserType, AppUser } from "../../api/appUsers";
import { useNotification } from "../../context/NotificationContext";
import styles from "./AppUsers.module.css";
import studio from "../../styles/studioPage.module.css";
import adminSurface from "../../components/layouts/AdminSurface.module.css";

const TYPE_FILTER_OPTIONS = [
    { label: "appUserTypeApp", value: "app" },
    { label: "appUserTypeAdmin", value: "ADMIN" },
];

const AppUsers: React.FC = () => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { showNotification } = useNotification();

    const [users, setUsers] = useState<AppUser[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);

    const [search, setSearch] = useState<string>("");
    const [searchDebounced, setSearchDebounced] = useState<string>("");
    const [selectedType, setSelectedType] = useState<string>("");

    const [page, setPage] = useState<number>(1);
    const [limit] = useState<number>(15);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [totalCount, setTotalCount] = useState<number>(0);

    const typeOptions = useMemo(
        () =>
            TYPE_FILTER_OPTIONS.map((opt) => ({
                label: t(opt.label) || opt.label,
                value: opt.value,
            })),
        [t],
    );

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchDebounced(search);
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        setPage(1);
    }, [searchDebounced, selectedType]);

    const fetchUsers = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            if (!token) return;

            const response = await getAppUsers({
                token,
                page,
                limit,
                search: searchDebounced || undefined,
                type: selectedType || undefined,
            });

            setUsers(response.users);
            setTotalCount(response.pagination.totalCount);
            setTotalPages(response.pagination.totalPages);
        } catch (error: any) {
            console.error("Error fetching app users:", error);
            if (error.statusCode === 401 || error.statusCode === 403) {
                showNotification({
                    message: t("appUsersUnauthorized") || "Unauthorized",
                    type: "error",
                });
            } else {
                showNotification({
                    message: t("errorFetchingUsers") || "Error fetching users",
                    type: "error",
                });
            }
        } finally {
            setLoading(false);
        }
    }, [page, limit, searchDebounced, selectedType, showNotification, t]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleResetFilters = () => {
        setSearch("");
        setSearchDebounced("");
        setSelectedType("");
        setPage(1);
    };

    const handleToggleAdmin = async (user: AppUser) => {
        const newType = user.type === "ADMIN" ? "app" : "ADMIN";
        const confirmMessage =
            newType === "ADMIN"
                ? t("confirmSetAsAdmin", { email: user.email }) || `Set ${user.email} as app admin?`
                : t("confirmRemoveAdmin", { email: user.email }) || `Remove admin status from ${user.email}?`;

        if (!window.confirm(confirmMessage)) return;

        try {
            setUpdatingUserId(user.id);
            const token = localStorage.getItem("token");
            if (!token) return;

            await updateAppUserType({ token, userId: user.id, type: newType });
            await fetchUsers();

            showNotification({
                message:
                    newType === "ADMIN"
                        ? t("setAsAdminSuccess") || "User promoted to admin"
                        : t("removeAdminSuccess") || "Admin status removed",
                type: "success",
            });
        } catch (error) {
            console.error("Error updating user type:", error);
            showNotification({
                message: t("errorUpdatingUserType") || "Error updating user type",
                type: "error",
            });
        } finally {
            setUpdatingUserId(null);
        }
    };

    const visiblePages = useMemo(() => {
        const range: (number | string)[] = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) range.push(i);
        } else {
            range.push(1);
            if (page > 3) range.push("...");
            const start = Math.max(2, page - 1);
            const end = Math.min(totalPages - 1, page + 1);
            for (let i = start; i <= end; i++) range.push(i);
            if (page < totalPages - 2) range.push("...");
            range.push(totalPages);
        }
        return range;
    }, [page, totalPages]);

    const startItem = totalCount === 0 ? 0 : (page - 1) * limit + 1;
    const endItem = Math.min(page * limit, totalCount);

    const formatDate = (dateString: string | null) => {
        if (!dateString) return "—";
        const date = new Date(dateString);
        return date.toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const displayName = (user: AppUser) => {
        const name = `${user.firstname || ""} ${user.lastname || ""}`.trim();
        return name || t("anonymousUser", { defaultValue: "Utilisateur anonyme" });
    };

    return (
        <div className={adminSurface.pageContent}>
            <div className={`${studio.pageTitle} ${adminSurface.pageTitle}`}>
                <TitleSubtitle
                    title={t("appUsersTitle") || "App Users"}
                    subtitle={t("appUsersSubtitle") || "Manage app users and admin privileges"}
                />
            </div>

            <div className={styles.kpi}>
                <span className={styles.kpiLabel}>{t("totalUsers") || "Total Users"}</span>
                <span className={styles.kpiValue}>{totalCount.toLocaleString()}</span>
            </div>

            <div className={adminSurface.tblBar}>
                <div className={adminSurface.searchWrap}>
                    <Icon name="ic-search" className={adminSurface.searchWrapIcon} size={18} />
                    <TextInput
                        customClassName={adminSurface.searchInput}
                        placeholder={t("searchUser") || "Search by email or name"}
                        onChange={(e) => setSearch(e.target.value)}
                        value={search}
                        disabled={false}
                    />
                </div>
                <div className={styles.typeSelect}>
                    <Dropdown
                        label=""
                        value={selectedType}
                        options={typeOptions}
                        onChange={setSelectedType}
                        placeholder={t("appUserTypeAll") || "All types"}
                        loading={false}
                        error={false}
                        isWithoutMargin
                        fullWidth
                    />
                </div>
                <button
                    type="button"
                    className={`${adminSurface.modifyButton} ${styles.resetBtn}`}
                    onClick={handleResetFilters}
                >
                    {t("resetFilters") || "Reset"}
                </button>
            </div>

            <div className={adminSurface.tableCard} style={{ marginTop: 0 }}>
                {loading ? (
                    <div className={styles.loading}>
                        <div className={styles.spinner} />
                    </div>
                ) : users.length === 0 ? (
                    <div className={styles.empty}>
                        <div className={styles.emptyTitle}>{t("noUsersFound") || "No users found"}</div>
                        <div className={styles.emptyText}>
                            {t("noUsersFoundDescription") || "Try adjusting your filters or check back later."}
                        </div>
                    </div>
                ) : (
                    <div className={adminSurface.tblScrollBody}>
                        <table className={adminSurface.table}>
                            <thead>
                                <tr>
                                    <th>{t("createdAt") || "Created"}</th>
                                    <th>{t("email") || "Email"}</th>
                                    <th>{t("name") || "Name"}</th>
                                    <th>{t("phone") || "Phone"}</th>
                                    <th>{t("appUserType") || "Type"}</th>
                                    <th>{t("status") || "Status"}</th>
                                    <th className={styles.actCell}>{t("actions") || "Actions"}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((user) => (
                                    <tr key={user.id}>
                                        <td style={{ whiteSpace: "nowrap" }}>{formatDate(user.createdAt)}</td>
                                        <td className={styles.emailCell}>{user.email}</td>
                                        <td className={styles.nameCell}>{displayName(user)}</td>
                                        <td>{user.phone || "—"}</td>
                                        <td>
                                            <span className={styles.tag}>
                                                {user.type === "ADMIN"
                                                    ? t("appUserTypeAdmin") || "Admin"
                                                    : t("appUserTypeApp") || "App"}
                                            </span>
                                        </td>
                                        <td>
                                            <span
                                                className={`${styles.tag} ${
                                                    user.status === "DELETED" ? styles.tagMuted : styles.tagOk
                                                }`}
                                            >
                                                {user.status === "DELETED"
                                                    ? t("appUserStatusDeleted") || "Deleted"
                                                    : t("appUserStatusActive") || "Active"}
                                            </span>
                                        </td>
                                        <td className={styles.actCell}>
                                            <button
                                                type="button"
                                                className={`${adminSurface.modifyButton} ${styles.rowBtn}`}
                                                disabled={updatingUserId === user.id}
                                                onClick={() => handleToggleAdmin(user)}
                                            >
                                                {updatingUserId === user.id
                                                    ? "..."
                                                    : user.type === "ADMIN"
                                                      ? t("removeAdmin") || "Remove admin"
                                                      : t("setAsAdmin") || "Set as admin"}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className={adminSurface.tblFoot}>
                    <span>
                        {t("displayedUsers") || "Displayed"}:{" "}
                        {`${startItem}–${endItem} / ${totalCount.toLocaleString()}`}
                    </span>
                    {totalPages > 1 && (
                        <div className={styles.pager}>
                            <button
                                type="button"
                                className={styles.pagerBtn}
                                disabled={page === 1}
                                onClick={() => setPage((p) => p - 1)}
                            >
                                ‹
                            </button>
                            {visiblePages.map((p, idx) =>
                                p === "..." ? (
                                    <span key={`ellipsis-${idx}`} className={styles.pagerEllipsis}>
                                        …
                                    </span>
                                ) : (
                                    <button
                                        type="button"
                                        key={p}
                                        className={`${styles.pagerBtn}${p === page ? ` ${styles.pagerBtnActive}` : ""}`}
                                        onClick={() => setPage(p as number)}
                                    >
                                        {p}
                                    </button>
                                ),
                            )}
                            <button
                                type="button"
                                className={styles.pagerBtn}
                                disabled={page === totalPages}
                                onClick={() => setPage((p) => p + 1)}
                            >
                                ›
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AppUsers;
